<template>
  <div class="catalog-page">
    <!-- Page Header with Search -->
    <header class="catalog-header">
      <div class="catalog-header__bg"></div>
      <div class="catalog-header__container">
        <nav class="catalog-header__breadcrumb" aria-label="Breadcrumb">
          <NuxtLink to="/" class="catalog-header__breadcrumb-link">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
            </svg>
            {{ t('nav.home') || 'Home' }}
          </NuxtLink>
          <span class="catalog-header__breadcrumb-sep">
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </span>
          <span class="catalog-header__breadcrumb-current">{{ t('spareParts.title') || 'Spare Parts' }}</span>
        </nav>
        
        <div class="catalog-header__row">
          <div class="catalog-header__left">
            <h1 class="catalog-header__title">{{ t('spareParts.pageTitle') || 'Spare Parts' }}</h1>
            <p class="catalog-header__subtitle">{{ t('spareParts.pageDescription') || 'Quality replacement parts for your machines' }}</p>
            <div class="catalog-header__stats">
              <div class="catalog-header__stat">
                <span class="catalog-header__stat-number">{{ sparePartsCount }}</span>
                <span class="catalog-header__stat-label">{{ t('spareParts.results.total') || 'Parts' }}</span>
              </div>
            </div>
          </div>
          
          <!-- Search Bar - Right Side -->
          <div class="catalog-header__search">
            <div class="catalog-search">
              <svg class="catalog-search__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="t('spareParts.search.placeholder') || 'Search spare parts...'"
                class="catalog-search__input"
                @input="debouncedSearch"
              />
              <button
                v-if="searchQuery"
                @click="clearSearch"
                class="catalog-search__clear"
                :aria-label="t('spareParts.search.clear') || 'Clear search'"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Filters Section -->
    <section class="catalog-filters">
      <div class="catalog-filters__container">
        <!-- Desktop: All in one row -->
        <div class="catalog-filters__row">
          <!-- Filter Pills -->
          <div class="catalog-filters__grid">
            <!-- Brand Filter -->
            <div class="catalog-filter">
              <select v-model="filters.brand_id" @change="searchSpareParts" class="catalog-filter__select">
                <option value="">{{ t('spareParts.filters.allBrands') || 'All Brands' }}</option>
                <option v-for="brand in availableFilters.brands" :key="brand.id" :value="brand.id">{{ brand.name }}</option>
              </select>
            </div>

            <!-- Stock Status Filter -->
            <div class="catalog-filter">
              <select v-model="filters.stock_status" @change="searchSpareParts" class="catalog-filter__select">
                <option value="">{{ t('spareParts.filters.anyStatus') || 'Stock Status' }}</option>
                <option value="in_stock">{{ t('spareParts.stockStatus.inStock') || 'In Stock' }}</option>
                <option value="low_stock">{{ t('spareParts.stockStatus.lowStock') || 'Low Stock' }}</option>
                <option value="out_of_stock">{{ t('spareParts.stockStatus.outOfStock') || 'Out of Stock' }}</option>
              </select>
            </div>
          </div>

          <!-- Actions: Clear + Sort -->
          <div class="catalog-filters__actions">
            <button
              v-if="activeFiltersCount > 0"
              @click="clearFilters"
              class="catalog-btn catalog-btn--danger"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
              {{ t('spareParts.filters.clear') || 'Clear' }}
            </button>
            
            <div class="catalog-sort-dropdown" :class="{ 'is-open': sortDropdownOpen }">
              <button @click.stop="sortDropdownOpen = !sortDropdownOpen" class="catalog-sort-dropdown__trigger">
                <svg class="catalog-sort-dropdown__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/>
                </svg>
                <span class="catalog-sort-dropdown__label">{{ currentSortLabel }}</span>
                <svg class="catalog-sort-dropdown__chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              <div class="catalog-sort-dropdown__menu" v-show="sortDropdownOpen" @click.stop>
                <button 
                  v-for="option in sparePartsSortOptions" 
                  :key="option.value"
                  @click="selectSort(option.value)"
                  class="catalog-sort-dropdown__item"
                  :class="{ 'is-active': sortBy === option.value }"
                >
                  <svg class="catalog-sort-dropdown__item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="option.icon"/>
                  </svg>
                  <span class="catalog-sort-dropdown__item-label">{{ option.label }}</span>
                  <svg v-if="sortBy === option.value" class="catalog-sort-dropdown__check" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Filter Toggle -->
        <button
          @click="toggleFilters"
          class="catalog-filters__toggle"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
          </svg>
          <span>{{ t('spareParts.filters.filtersTitle') || 'Filters' }}</span>
          <span v-if="activeFiltersCount > 0" class="catalog-filters__badge">{{ activeFiltersCount }}</span>
        </button>

        <!-- Mobile Filter Panel -->
        <div 
          class="catalog-filters__controls"
          :class="{ 'catalog-filters__controls--visible': filtersVisible }"
        >
          <div class="catalog-filters__grid">
            <div class="catalog-filter">
              <select v-model="filters.brand_id" @change="searchSpareParts" class="catalog-filter__select">
                <option value="">{{ t('spareParts.filters.allBrands') || 'All Brands' }}</option>
                <option v-for="brand in availableFilters.brands" :key="brand.id" :value="brand.id">{{ brand.name }}</option>
              </select>
            </div>
            <div class="catalog-filter">
              <select v-model="filters.stock_status" @change="searchSpareParts" class="catalog-filter__select">
                <option value="">{{ t('spareParts.filters.anyStatus') || 'Stock Status' }}</option>
                <option value="in_stock">{{ t('spareParts.stockStatus.inStock') || 'In Stock' }}</option>
                <option value="low_stock">{{ t('spareParts.stockStatus.lowStock') || 'Low Stock' }}</option>
                <option value="out_of_stock">{{ t('spareParts.stockStatus.outOfStock') || 'Out of Stock' }}</option>
              </select>
            </div>
          </div>
          <div class="catalog-filters__actions">
            <button
              v-if="activeFiltersCount > 0"
              @click="clearFilters"
              class="catalog-btn catalog-btn--danger"
            >
              {{ t('spareParts.filters.clearAll') || 'Clear Filters' }}
            </button>
            <div class="catalog-sort-dropdown" :class="{ 'is-open': sortDropdownOpen }">
              <button @click.stop="sortDropdownOpen = !sortDropdownOpen" class="catalog-sort-dropdown__trigger">
                <svg class="catalog-sort-dropdown__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"/>
                </svg>
                <span class="catalog-sort-dropdown__label">{{ currentSortLabel }}</span>
                <svg class="catalog-sort-dropdown__chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              <div class="catalog-sort-dropdown__menu" v-show="sortDropdownOpen" @click.stop>
                <button 
                  v-for="option in sparePartsSortOptions" 
                  :key="option.value"
                  @click="selectSort(option.value)"
                  class="catalog-sort-dropdown__item"
                  :class="{ 'is-active': sortBy === option.value }"
                >
                  <svg class="catalog-sort-dropdown__item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="option.icon"/>
                  </svg>
                  <span class="catalog-sort-dropdown__item-label">{{ option.label }}</span>
                  <svg v-if="sortBy === option.value" class="catalog-sort-dropdown__check" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Results Info Bar -->
    <div class="catalog-results-bar">
      <div class="catalog-results-bar__container">
        <p class="catalog-results-bar__info">
          <span class="catalog-results-bar__count">{{ sparePartsCount }}</span>
          {{ sparePartsCount === 1 ? t('spareParts.results.foundSingular') : t('spareParts.results.foundPlural') }}
          <span v-if="pagination" class="catalog-results-bar__pagination">
            · {{ t('spareParts.results.page') || 'Page' }} {{ pagination.current_page }} {{ t('spareParts.results.of') || 'of' }} {{ pagination.last_page }}
          </span>
        </p>
        <div class="catalog-results-bar__per-page">
          <span class="catalog-results-bar__per-page-label">{{ t('spareParts.results.show') || 'Show' }}:</span>
          <div class="catalog-results-bar__per-page-buttons">
            <button
              v-for="option in [12, 24, 48]"
              :key="option"
              @click="handlePerPageChange(option)"
              :class="[
                'catalog-results-bar__per-page-btn',
                { 'catalog-results-bar__per-page-btn--active': perPage === option }
              ]"
            >
              {{ option }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <main class="catalog-main">
      <div class="catalog-main__container">
        <!-- Loading State -->
        <div v-if="loading" class="catalog-loading">
          <div v-for="i in 4" :key="i" class="catalog-skeleton">
            <div class="catalog-skeleton__image"></div>
            <div class="catalog-skeleton__content">
              <div class="catalog-skeleton__info">
                <div class="catalog-skeleton__title"></div>
                <div class="catalog-skeleton__subtitle"></div>
                <div class="catalog-skeleton__specs">
                  <div class="catalog-skeleton__spec"></div>
                  <div class="catalog-skeleton__spec"></div>
                  <div class="catalog-skeleton__spec"></div>
                </div>
              </div>
              <div class="catalog-skeleton__status"></div>
              <div class="catalog-skeleton__actions">
                <div class="catalog-skeleton__btn"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="sparePartsCount === 0" class="catalog-empty">
          <div class="catalog-empty__icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <h3 class="catalog-empty__title">{{ t('spareParts.results.noResults') || 'No spare parts found' }}</h3>
          <p class="catalog-empty__text">{{ t('spareParts.results.noResultsMessage') || 'Try adjusting your filters or search criteria' }}</p>
          <button @click="clearFilters" class="catalog-btn catalog-btn--primary">
            {{ t('spareParts.filters.clearAll') || 'Clear All Filters' }}
          </button>
        </div>

        <!-- Results Grid -->
        <div v-else class="catalog-grid">
          <SparePartListCard
            v-for="part in spareParts"
            :key="part.id"
            :part="part"
            @inquire="showPartInquiryModal($event)"
          />
        </div>

        <!-- Pagination -->
        <div v-if="sparePartsCount > 0" class="catalog-pagination">
          <Pagination
            v-model:current-page="currentPage"
            :total-items="sparePartsCount"
            :items-per-page="perPage"
            :pagination="pagination"
            @update:current-page="handlePageChange"
          />
        </div>
      </div>
    </main>

    <!-- Inquiry Modal -->
    <SparePartInquiryModal
      v-if="showInquiryModal"
      :part="selectedPartForInquiry"
      @close="showInquiryModal = false"
    />
  </div>
</template>

<script setup>
import { useDebounceFn } from '@vueuse/core'
import Pagination from "~/components/ui/Pagination.vue"
import SparePartListCard from "~/components/spare-parts/SparePartListCard.vue"
import SparePartInquiryModal from "~/components/spare-parts/SparePartInquiryModal.vue"
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n();

const showInquiryModal = ref(false);
const selectedPartForInquiry = ref(null);

const showPartInquiryModal = (part) => {
  selectedPartForInquiry.value = part;
  showInquiryModal.value = true;
};

useHead({
  title: computed(() => t('meta.sparePartsTitle') || 'Spare Parts - Technomac SNC'),
  meta: [
    { name: 'description', content: computed(() => t('meta.sparePartsDescription') || 'Quality spare parts for printing equipment') }
  ]
})

const { $api } = useNuxtApp()
const spareParts = ref([])
const loading = ref(true)
const filtersVisible = ref(false)
const searchQuery = ref('')
const filters = ref({
  brand_id: '',
  stock_status: ''
})
const sortBy = ref('created_at')
const sortDropdownOpen = ref(false)
const availableFilters = ref({
  brands: []
})
const pagination = ref(null)
const currentPage = ref(1)
const perPage = ref(12)

const activeFiltersCount = computed(() => {
  return Object.values(filters.value).filter(value => value !== '' && value !== null).length
})

const sparePartsCount = computed(() => {
  return pagination.value?.total || spareParts.value?.length || 0
})

const sparePartsSortOptions = computed(() => [
  { value: 'created_at', label: t('spareParts.sorting.latestArrivals'), icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' },
  { value: 'name', label: t('spareParts.sorting.nameAZ'), icon: 'M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12' },
  { value: 'stock_quantity', label: t('spareParts.sorting.stockHighToLow'), icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' }
])

const currentSortLabel = computed(() => {
  const option = sparePartsSortOptions.value.find(opt => opt.value === sortBy.value)
  return option ? option.label : t('spareParts.sorting.latestArrivals')
})

const toggleFilters = () => {
  filtersVisible.value = !filtersVisible.value
}

const selectSort = (value) => {
  sortBy.value = value
  sortDropdownOpen.value = false
  handleSortChange()
}

// Close dropdown when clicking outside
const handleClickOutside = (e) => {
  const dropdowns = document.querySelectorAll('.catalog-sort-dropdown')
  let clickedInside = false
  dropdowns.forEach(dropdown => {
    if (dropdown.contains(e.target)) {
      clickedInside = true
    }
  })
  if (!clickedInside) {
    sortDropdownOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const clearSearch = () => {
  searchQuery.value = ''
  debouncedSearch()
}

const debouncedSearch = useDebounceFn(() => {
  searchSpareParts()
}, 500)

const searchSpareParts = async () => {
  loading.value = true

  try {
    const params = {
      ...filters.value,
      search: searchQuery.value,
      sort_by: sortBy.value,
      per_page: perPage.value,
      page: currentPage.value
    }

    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null) {
        delete params[key]
      }
    })

    if (params.brand_id && params.brand_id !== '') {
      params.spare_part_brand = params.brand_id
      delete params.brand_id
    }

    if (params.stock_status && params.stock_status !== '') {
      params.spare_part_status = params.stock_status
      delete params.stock_status
    }

    const endpoint = (Object.keys(params).length > 0 || searchQuery.value) ? '/spare-parts/search' : '/spare-parts'

    const apiParams = { ...params, per_page: perPage.value, sort_by: sortBy.value, page: currentPage.value }
    const response = await $api.get(endpoint, apiParams)

    if (response.data && Array.isArray(response.data.data)) {
      spareParts.value = response.data.data
      pagination.value = response.data.pagination
    } else if (Array.isArray(response.data)) {
      spareParts.value = response.data
      pagination.value = null
    } else {
      spareParts.value = []
      pagination.value = null
    }
  } catch (error) {
    spareParts.value = []
  } finally {
    loading.value = false
  }
}

const clearFilters = () => {
  filters.value = {
    brand_id: '',
    stock_status: ''
  }
  searchQuery.value = ''
  currentPage.value = 1
  searchSpareParts()
}

const handleSortChange = () => {
  currentPage.value = 1
  searchSpareParts()
}

const handlePerPageChange = (value) => {
  perPage.value = value
  currentPage.value = 1
  searchSpareParts()
}

const handlePageChange = (page) => {
  currentPage.value = page
  searchSpareParts()
  nextTick(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })
}

const fetchFilters = async () => {
  try {
    const response = await $api.get('/spare-parts/filters')

    if (response.data && response.data.data) {
      availableFilters.value = response.data.data
    } else {
      availableFilters.value = response.data || {}
    }
  } catch (error) {
    availableFilters.value = {}
  }
}

watch(sortBy, () => {
  currentPage.value = 1
  searchSpareParts()
})

watch(perPage, () => {
  currentPage.value = 1
  searchSpareParts()
})

watch(currentPage, () => {
  searchSpareParts()
})

onMounted(async () => {
  try {
    await fetchFilters()
    await searchSpareParts()
  } catch (error) {
    // Handle error silently
  }
})
</script>

<style scoped>
/* ============================================
   SPARE PARTS CATALOG PAGE - Modern Industrial Design
   Identical to Machines page with Emerald accent
   ============================================ */

.catalog-page {
  min-height: 100vh;
  background: #f1f5f9;
  font-family: 'Open Sans', system-ui, sans-serif;
}

/* ============================================
   HEADER - Bold with Background
   ============================================ */

.catalog-header {
  position: relative;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  padding: 40px 0 48px;
  overflow: hidden;
}

.catalog-header__bg {
  position: absolute;
  inset: 0;
  background-image: 
    radial-gradient(circle at 20% 80%, rgba(16, 185, 129, 0.15) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(52, 211, 153, 0.1) 0%, transparent 40%);
  pointer-events: none;
}

.catalog-header__container {
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  z-index: 1;
}

.catalog-header__breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 24px;
}

.catalog-header__breadcrumb-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.catalog-header__breadcrumb-link:hover {
  color: #ffffff;
}

.catalog-header__breadcrumb-sep {
  color: rgba(255, 255, 255, 0.3);
  display: flex;
}

.catalog-header__breadcrumb-current {
  color: #ffffff;
  font-weight: 600;
}

.catalog-header__row {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media (min-width: 900px) {
  .catalog-header__row {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
  }
}

.catalog-header__left {
  flex: 1;
}

.catalog-header__title {
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

@media (min-width: 768px) {
  .catalog-header__title {
    font-size: 42px;
  }
}

.catalog-header__subtitle {
  margin: 8px 0 0 0;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.65);
  max-width: 400px;
  line-height: 1.4;
}

.catalog-header__stats {
  display: inline-flex;
  margin-top: 16px;
}

.catalog-header__stat {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50px;
}

.catalog-header__stat-number {
  font-size: 24px;
  font-weight: 800;
  color: #ffffff;
  line-height: 1;
}

.catalog-header__stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* Search in Header - Right Side */
.catalog-header__search {
  flex-shrink: 0;
  width: 100%;
}

@media (min-width: 900px) {
  .catalog-header__search {
    width: 380px;
  }
}

/* ============================================
   FILTERS SECTION - Modern Pills Style
   ============================================ */

.catalog-filters {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 40;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.catalog-filters__container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 24px;
}

/* Search Bar - Prominent Style */
.catalog-search {
  position: relative;
  display: flex;
  align-items: center;
}

.catalog-search__icon {
  position: absolute;
  left: 18px;
  width: 22px;
  height: 22px;
  color: #ffffff;
  pointer-events: none;
  z-index: 2;
}

.catalog-search__input {
  width: 100%;
  padding: 16px 50px 16px 52px;
  font-size: 15px;
  font-weight: 500;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  color: #ffffff;
  transition: all 0.2s ease;
}

.catalog-search__input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.1);
}

.catalog-search__input::placeholder {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
}

.catalog-search__clear {
  position: absolute;
  right: 14px;
  padding: 6px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.catalog-search__clear:hover {
  background: rgba(255, 255, 255, 0.3);
}

.catalog-search__clear svg {
  width: 16px;
  height: 16px;
  color: #ffffff;
}

/* Mobile Filter Toggle */
.catalog-filters__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;
}

.catalog-filters__toggle:hover {
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  border-color: #cbd5e1;
}

.catalog-filters__toggle svg {
  width: 20px;
  height: 20px;
  color: #64748b;
}

.catalog-filters__badge {
  background: #10b981;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
}

/* Hide mobile toggle on desktop, show row */
@media (min-width: 769px) {
  .catalog-filters__toggle {
    display: none;
  }
  
  .catalog-filters__row {
    display: flex;
  }
}

/* Hide row on mobile, show toggle */
@media (max-width: 768px) {
  .catalog-filters__row {
    display: none;
  }
  
  .catalog-filters__toggle {
    display: flex;
  }
}

/* Filter Controls */
.catalog-filters__controls {
  display: none;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.catalog-filters__controls--visible {
  display: block;
}

@media (min-width: 769px) {
  .catalog-filters__controls {
    display: none !important;
  }
}

.catalog-filters__row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  padding-bottom: 4px;
}

.catalog-filters__grid {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .catalog-filters__grid {
    display: none;
  }
  
  .catalog-filters__controls .catalog-filters__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    width: 100%;
  }
  
  .catalog-filters__controls .catalog-filter {
    width: 100%;
  }
  
  .catalog-filters__controls .catalog-filter__select {
    width: 100%;
  }
}

/* Individual Filter - Compact Pill */
.catalog-filter {
  flex-shrink: 0;
}

.catalog-filter__label {
  display: none;
}

.catalog-filter__select {
  padding: 10px 36px 10px 14px;
  font-size: 13px;
  font-weight: 600;
  border: 2px solid #cbd5e1;
  border-radius: 50px;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 14px;
  white-space: nowrap;
}

.catalog-filter__select:hover {
  border-color: #10b981;
  background-color: #f8fafc;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.catalog-filter__select:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.15);
}

/* Filter Actions - Inline */
.catalog-filters__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  margin-left: auto;
}

@media (max-width: 768px) {
  .catalog-filters__actions {
    display: none;
  }
  
  .catalog-filters__controls .catalog-filters__actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    width: 100%;
    margin-left: 0;
    margin-top: 16px;
  }
  
  .catalog-filters__controls .catalog-sort-dropdown {
    width: 100%;
  }
  
  .catalog-filters__controls .catalog-sort-dropdown__trigger {
    width: 100%;
    justify-content: center;
  }
  
  .catalog-filters__controls .catalog-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Sort Dropdown - Modern Design (Emerald Theme) */
.catalog-sort-dropdown {
  position: relative;
  z-index: 50;
}

.catalog-sort-dropdown__trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  color: #334155;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.catalog-sort-dropdown__trigger:hover {
  border-color: #10b981;
  background: linear-gradient(135deg, #ffffff 0%, #ecfdf5 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.15);
  transform: translateY(-1px);
}

.catalog-sort-dropdown.is-open .catalog-sort-dropdown__trigger {
  border-color: #10b981;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
}

.catalog-sort-dropdown__icon {
  width: 16px;
  height: 16px;
  color: #10b981;
  flex-shrink: 0;
}

.catalog-sort-dropdown__label {
  flex: 1;
  text-align: left;
  white-space: nowrap;
}

.catalog-sort-dropdown__chevron {
  width: 14px;
  height: 14px;
  color: #94a3b8;
  transition: transform 0.25s ease;
  flex-shrink: 0;
}

.catalog-sort-dropdown.is-open .catalog-sort-dropdown__chevron {
  transform: rotate(180deg);
  color: #10b981;
}

.catalog-sort-dropdown__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 8px;
  animation: dropdownFadeIn 0.2s ease;
}

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.catalog-sort-dropdown__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  background: transparent;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
}

.catalog-sort-dropdown__item:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.catalog-sort-dropdown__item.is-active {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  color: #047857;
}

.catalog-sort-dropdown__item-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  color: #64748b;
}

.catalog-sort-dropdown__item-label {
  flex: 1;
}

.catalog-sort-dropdown__check {
  width: 16px;
  height: 16px;
  color: #10b981;
}

/* Buttons */
.catalog-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 50px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}

.catalog-btn svg {
  width: 14px;
  height: 14px;
}

.catalog-btn--primary {
  background: #10b981;
  color: #ffffff;
}

.catalog-btn--primary:hover {
  background: #059669;
}

.catalog-btn--danger {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

.catalog-btn--danger:hover {
  background: #fee2e2;
}

/* ============================================
   RESULTS BAR - Subtle
   ============================================ */

.catalog-results-bar {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 12px 0;
}

.catalog-results-bar__container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

@media (min-width: 640px) {
  .catalog-results-bar__container {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.catalog-results-bar__info {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.catalog-results-bar__count {
  font-weight: 700;
  color: #0f172a;
}

.catalog-results-bar__pagination {
  color: #94a3b8;
}

.catalog-results-bar__per-page {
  display: flex;
  align-items: center;
  gap: 10px;
}

.catalog-results-bar__per-page-label {
  font-size: 13px;
  color: #64748b;
}

.catalog-results-bar__per-page-buttons {
  display: flex;
  gap: 4px;
}

.catalog-results-bar__per-page-btn {
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  background: #ffffff;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.catalog-results-bar__per-page-btn:hover {
  border-color: #cbd5e1;
  color: #374151;
}

.catalog-results-bar__per-page-btn--active {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

/* ============================================
   MAIN CONTENT
   ============================================ */

.catalog-main {
  padding: 32px 0 64px;
}

.catalog-main__container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Product Grid */
.catalog-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.catalog-grid > * {
  width: 100%;
}

/* Loading Skeleton */
.catalog-loading {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.catalog-skeleton {
  display: flex;
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  height: 160px;
}

@media (max-width: 768px) {
  .catalog-skeleton {
    flex-direction: column;
    height: auto;
  }
}

.catalog-skeleton__image {
  width: 280px;
  flex-shrink: 0;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@media (max-width: 768px) {
  .catalog-skeleton__image {
    width: 100%;
    height: 180px;
  }
}

.catalog-skeleton__content {
  flex: 1;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
}

@media (max-width: 768px) {
  .catalog-skeleton__content {
    flex-direction: column;
    align-items: stretch;
  }
}

.catalog-skeleton__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.catalog-skeleton__title {
  height: 20px;
  width: 60%;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.catalog-skeleton__subtitle {
  height: 16px;
  width: 30%;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.catalog-skeleton__specs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-top: 6px;
}

.catalog-skeleton__spec {
  height: 16px;
  width: 80px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.catalog-skeleton__status {
  width: 80px;
  height: 26px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 20px;
  flex-shrink: 0;
}

.catalog-skeleton__actions {
  flex-shrink: 0;
}

.catalog-skeleton__btn {
  height: 40px;
  width: 140px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .catalog-skeleton__btn {
    width: 100%;
  }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Empty State */
.catalog-empty {
  text-align: center;
  padding: 64px 24px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.catalog-empty__icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.catalog-empty__icon svg {
  width: 32px;
  height: 32px;
  color: #94a3b8;
}

.catalog-empty__title {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.catalog-empty__text {
  margin: 0 0 24px 0;
  font-size: 15px;
  color: #64748b;
  max-width: 360px;
  margin-left: auto;
  margin-right: auto;
}

/* Pagination */
.catalog-pagination {
  margin-top: 40px;
}
</style>
